import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';

import {
  OnBoard,
  ALL_LOADED,
  FriendsLoaded,
  LeaderboardLoaded,
  Loader,
  SeasonInfoLoaded,
  UserLoaded,
  useCollect,
  useMe,
  useSeasonInfo,
  useFriends,
  useHackathonProjects,
  useLeaderboard,
  verifyOtp,
  HackathonProjectsLoaded,
  useFantomRun
} from '@/shared';
import {
  INVALID_AUTH_DATA,
  useAuthData,
  AuthProviderModal,
  AuthProvider,
  StandaloneOtpScreen
} from '@/features';
import LoadingLogo from '@/shared/assets/logos/loading-logo.svg';
import { getDevicePlatform } from '@/utils';

import { PageHeader, PageFooter } from './components';
import { Container, Content, FooterContainer } from './authorized-layout.styled';

export interface AuthorizedLayoutProps {
  children?: React.ReactNode;
  title?: string;
  renderHeader?: () => React.ReactNode;
  hasNavBar?: boolean;
  hasFooter?: boolean;
  hasBackButtonTo?: number | string;
  showBreadcrumb?: boolean;
}

const BottomSheetModalStyle = createGlobalStyle`
    .bottom-sheet-portal .ReactModal__Content.ReactModal__Content {
        border-left: 2px solid #41424e !important;
        border-right: 2px solid #41424e !important;
        border-bottom: 2px solid #41424e !important;
        border-radius: 10px !important;
    }
`;

const UserNotSuspicious = 0;
const UserSuspicious = 1;
const UserCaptchaSolved = 2;

const AUTH_PROVIDERS: AuthProvider[] = [
  {
    id: 'telegram',
    name: 'Telegram',
    icon: '/icons/telegram.svg',
    redirectUrl: window.config.telegram_bot_redirect_url
  },
  // {
  //   id: 'discord',
  //   name: 'Discord',
  //   icon: '/icons/discord.svg',
  //   redirectUrl: window.config.discord_auth_page,
  //   disabled: true
  // }
];

export const AuthorizedLayout: React.FC<AuthorizedLayoutProps> = (props) => {
  const { children, title } = props;
  const authData = useAuthData();
  const { collect } = useCollect();
  const meService = useMe(false);
  const { data: seasonData, refresh: seasonInfoRefresh } = useSeasonInfo(false);
  const { data: friendsData, refresh: friendsRefresh } = useFriends(false);
  const { userRankData, refresh: leaderboardRefresh } = useLeaderboard(false);
  const { items: hackathonItems, loading: hackathonLoading } = useHackathonProjects();
  const { refresh: fantomRunRefresh } = useFantomRun(false);
  const [showLoader, setShowLoader] = React.useState(true);
  const [loadingStatus, setLoadingStatus] = React.useState(0);
  const [isSuspicious, setIsSuspicious] = React.useState(UserNotSuspicious);

  const location = useLocation();
  const standalone = false; ///React.useMemo(() => isStandalonePWA(), []);

  // authProvider dfaults to AUTH_PROVIDERS[0] if only one is defined
  const [authProvider, setAuthProvider] = React.useState<string | null>(
    /*AUTH_PROVIDERS.length === 1 ? AUTH_PROVIDERS[0].id :*/ null
  );

  // Extract friendID from URL params for referral tracking
  const [friendID, setFriendID] = React.useState<string | null>(null);
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const friendIdParam = params.get('friendID');
    if (friendIdParam) setFriendID(friendIdParam);
  }, []);

  React.useEffect(() => {
    void collect({ url: '/auth' });
  }, [collect]);

  React.useLayoutEffect(() => {
    // this is needed to ensure the view scrolls to the top in every useNavigate()
    document.documentElement.scrollTo(0, 0);
  }, [location.pathname]);

  React.useEffect(() => {
    if (title) document.title = `${title}`;
  }, [title]);

  React.useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;

    root.classList.toggle('is-loading', showLoader);
    return () => root.classList.remove('is-loading');
  }, [showLoader]);

  React.useEffect(() => {
    // Ensure auth_provider is set before fetching user data
    if (authData.initData?.length
      && !authData.error && authData.authProvider) {
      meService.refresh({ withBalances: true });
    }
  }, [authData.initData]);

  React.useEffect(() => {
    let newLoadingStatus = 0;
    if (
      loadingStatus === 0 &&
      meService.data?.user.suspicious_activity &&
      isSuspicious === UserNotSuspicious
    ) {
      setIsSuspicious(UserSuspicious);
    } else if (
      meService.data?.user.id &&
      loadingStatus === 0 &&
      !meService.data?.user.suspicious_activity
    ) {
      newLoadingStatus |= UserLoaded;
      seasonInfoRefresh();
      friendsRefresh();
      leaderboardRefresh();
      fantomRunRefresh();
      setLoadingStatus(newLoadingStatus);
    }
  }, [meService, loadingStatus]);

  // remove loader screen when airdrop and task services have loaded data
  // in case of unauthorized access, the loader screen remains forever
  //
  useEffect(() => {
    if (!showLoader) return;
    if (!meService.data?.user.id) return;
    if (meService.data.user.suspicious_activity) return;
    if (isSuspicious === UserSuspicious) setIsSuspicious(UserCaptchaSolved);

    const intervalId = setInterval(() => {
      let newLoadingStatus = UserLoaded;

      if (seasonData) newLoadingStatus |= SeasonInfoLoaded;
      if (friendsData) newLoadingStatus |= FriendsLoaded;
      if (userRankData !== null && userRankData !== undefined)
        newLoadingStatus |= LeaderboardLoaded;
      if (!hackathonLoading) newLoadingStatus |= HackathonProjectsLoaded;
      if (newLoadingStatus === ALL_LOADED) {
        meService.clearInitDataFromUrl();
        // set user id in gtag
        window.gtag('set', 'user_id', meService.data?.user.id);
        clearInterval(intervalId);
      }
      setLoadingStatus(newLoadingStatus);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [meService.data?.user.id, seasonData, friendsData, userRankData, hackathonItems]);

  const loaderCompleted = () => setShowLoader(false);

  const handleCaptchaSolved = (catpchaToken: string) => {
    setIsSuspicious(UserCaptchaSolved);
    meService.sendCaptchaToken(catpchaToken);
  };

  const handleProviderSelect = (providerId: string) => {
    setAuthProvider(providerId);
  };

  const getRedirectUrl = (providerId: string) => {
    const provider = AUTH_PROVIDERS.find((p) => p.id === providerId);
    if (!provider) return '';

    const baseUrl = provider.redirectUrl;
    if (!friendID) return baseUrl;

    // Add friendID for referral tracking
    if (providerId === 'telegram') {
      return `${baseUrl}?start=${friendID}`;
    } else {
      return `${baseUrl}?friendID=${friendID}`;
    }
  };

  if (AUTH_PROVIDERS.length == 0) {
    return (
      <AuthProviderModal
        isOpen={true}
        logoSrc={LoadingLogo}
        providers={AUTH_PROVIDERS}
        selectedProvider={authProvider}
        onProviderSelect={handleProviderSelect}
        friendID={friendID}
      />
    );
  }

  if (authData.error === INVALID_AUTH_DATA && standalone) {
    return (
      <Container>
        <StandaloneOtpScreen
          verifyOtp={verifyOtp}
          onSuccess={() => {
            window.location.reload();
          }}
        />
      </Container>
    );
  }

  if (authData.error === INVALID_AUTH_DATA) {
    if (!authProvider) {
      return (
        <AuthProviderModal
          isOpen={true}
          logoSrc={LoadingLogo}
          providers={AUTH_PROVIDERS}
          selectedProvider={authProvider}
          onProviderSelect={handleProviderSelect}
          friendID={friendID}
        />
      );
    }
    const provider = AUTH_PROVIDERS.find((p) => p.id === authProvider);
    const redirectMessage = provider ? `Redirecting to ${provider.name}...` : 'Redirecting...';

    setTimeout(() => {
      const redirectUrl = getRedirectUrl(authProvider);
      window.location.href = redirectUrl;
    }, 1500);

    return (
      <Container>
        <Loader error={redirectMessage} loadingStatus={0} />
      </Container>
    );
  }

  const handleAuthErrorRetry = () => {
    localStorage.removeItem('session');
    localStorage.removeItem('auth_provider');
    window.location.reload();
  };

  // Handle OAuth errors (from Discord, etc.)
  if (authData.error && authData.error !== INVALID_AUTH_DATA) {
    return (
      <AuthProviderModal
        isOpen={true}
        providers={AUTH_PROVIDERS}
        selectedProvider={authProvider}
        onProviderSelect={handleProviderSelect}
        error={authData.error}
        onErrorRetry={handleAuthErrorRetry}
        friendID={friendID}
      />
    );
  }

  // Handle backend authentication errors (e.g., invalid Discord token)
  if (meService.error) {
    return (
      <AuthProviderModal
        isOpen={true}
        providers={AUTH_PROVIDERS}
        selectedProvider={authProvider}
        onProviderSelect={handleProviderSelect}
        error={meService.error}
        onErrorRetry={handleAuthErrorRetry}
        friendID={friendID}
      />
    );
  }

  if (showLoader) {
    return (
      <Container>
        <Loader
          loadingStatus={loadingStatus}
          onAnimationEnd={loaderCompleted}
          suspicious={isSuspicious === UserSuspicious}
          onCaptchaSolved={handleCaptchaSolved}
        />
      </Container>
    );
  }

  if (!meService.data?.user.onboarded) {
    if (window.config.onboard_ui_enabled) {
      return (
        <Container>
          <OnBoard />
        </Container>
      );
    } else {
      // if onboarding is disabled, complete onboarding automatically
      meService.completeOnboarding();
    }
  }

  return (
    <Container>
      <BottomSheetModalStyle />
      <PageHeader hasBackButtonTo={props.hasBackButtonTo} />
      <Content>{children}</Content>
      {props.hasFooter && getDevicePlatform() === 'android' && (
        <FooterContainer>
          <PageFooter />
        </FooterContainer>
      )}
      {props.hasFooter && getDevicePlatform() !== 'android' && (
          <PageFooter />
      )}
    </Container>
  );
};

export default AuthorizedLayout;
